import api from './api';
import { USER_ROLES } from '../constants/userRoles';

class ReservationService {
  /**
   * Crée une nouvelle réservation
   */
  async createReservation(reservationData) {
    try {
      const response = await api.post('/reservations', reservationData);
      return response;
    } catch (error) {
      console.error('Error creating reservation:', error);
      throw error;
    }
  }

  /**
   * Confirme une réservation
   */
  async confirmReservation(reservationId, confirmationData) {
    try {
      const response = await api.put(`/reservations/${reservationId}/confirm`, confirmationData);
      return response;
    } catch (error) {
      console.error('Error confirming reservation:', error);
      throw error;
    }
  }

  /**
   * Annule une réservation
   */
  async cancelReservation(reservationId, cancellationData) {
    try {
      const response = await api.put(`/reservations/${reservationId}/cancel`, cancellationData);
      return response;
    } catch (error) {
      console.error('Error canceling reservation:', error);
      throw error;
    }
  }

  /**
   * Active une réservation
   */
  async activateReservation(reservationId) {
    try {
      const response = await api.put(`/reservations/${reservationId}/activate`);
      return response;
    } catch (error) {
      console.error('Error activating reservation:', error);
      throw error;
    }
  }

  /**
   * Complète une réservation
   */
  async completeReservation(reservationId) {
    try {
      const response = await api.put(`/reservations/${reservationId}/complete`);
      return response;
    } catch (error) {
      console.error('Error completing reservation:', error);
      throw error;
    }
  }

  /**
   * Vérifie la disponibilité d'un listing
   */
  async checkAvailability(listingId, startDate, endDate) {
    try {
      const params = new URLSearchParams({
        listing_id: listingId,
        start_date: startDate,
        end_date: endDate
      });
      
      const response = await api.get(`/reservations/availability?${params}`);
      return response;
    } catch (error) {
      console.error('Error checking availability:', error);
      throw error;
    }
  }

  /**
   * Calcule le montant total d'une réservation
   */
  async calculateTotalAmount(listingId, startDate, endDate, options = {}) {
    try {
      const params = new URLSearchParams({
        listing_id: listingId,
        start_date: startDate,
        end_date: endDate,
        ...options
      });
      
      const response = await api.get(`/reservations/calculate-amount?${params}`);
      return response;
    } catch (error) {
      console.error('Error calculating total amount:', error);
      throw error;
    }
  }

  /**
   * Récupère les réservations d'un utilisateur
   */
  async getUserReservations(userId, options = {}) {
    try {
      // Ajouter l'userId aux options si fourni
      if (userId) {
        options.userId = userId;
      }

      if (options.forceRefresh) {
        options.force_refresh = '1';
        delete options.forceRefresh;
      }
      
      const params = new URLSearchParams(options);
      const response = await api.get(`/reservations/my-reservations?${params}`);
      return response;
    } catch (error) {
      console.error('Error getting user reservations:', error);
      throw error;
    }
  }

  /**
   * Récupère les réservations d'un propriétaire
   */
  async getOwnerReservations(ownerId, options = {}) {
    try {
      if (options.forceRefresh) {
        options.force_refresh = '1';
        delete options.forceRefresh;
      }
      const params = new URLSearchParams({
        role: USER_ROLES.OWNER,
        ...options
      });
      const response = await api.get(`/reservations/my-reservations?${params}`);
      return response.data; // ✅ Retourner response.data au lieu de response
    } catch (error) {
      console.error('Error getting owner reservations:', error);
      throw error;
    }
  }



  /**
   * Récupère les détails d'une réservation
   */
  async getReservationById(reservationId) {
    try {
      const response = await api.get(`/reservations/${reservationId}`);
      return response;
    } catch (error) {
      console.error('Error getting reservation:', error);
      throw error;
    }
  }

  /**
   * Met à jour une réservation
   */
  async updateReservation(reservationId, updateData) {
    try {
      const response = await api.put(`/reservations/${reservationId}`, updateData);
      return response;
    } catch (error) {
      console.error('Error updating reservation:', error);
      throw error;
    }
  }

  /**
   * Ajoute une note/évaluation à une réservation
   */
  async addReview(reservationId, reviewData) {
    try {
      const response = await api.post(`/reservations/${reservationId}/review`, reviewData);
      return response;
    } catch (error) {
      console.error('Error adding review:', error);
      throw error;
    }
  }

  /**
   * Vérifie les codes de réservation
   */
  async verifyCodes(reservationId, codes) {
    try {
      const response = await api.post(`/reservations/${reservationId}/verify-codes`, codes);
      return response;
    } catch (error) {
      console.error('Error verifying codes:', error);
      throw error;
    }
  }

  /**
   * Signale un dommage sur une réservation
   */
  async reportDamage(reservationId, damageData) {
    try {
      const response = await api.post(`/reservations/${reservationId}/damage`, damageData);
      return response;
    } catch (error) {
      console.error('Error reporting damage:', error);
      throw error;
    }
  }

  /**
   * Récupère l'historique d'une réservation
   */
  async getReservationHistory(reservationId) {
    try {
      const response = await api.get(`/reservations/${reservationId}/history`);
      return response;
    } catch (error) {
      console.error('Error getting reservation history:', error);
      throw error;
    }
  }
}

export const reservationService = new ReservationService();
